import { useState } from "react"
import { useRouter } from "expo-router"
import Icon from "@react-native-vector-icons/material-design-icons"
import { ActivityIndicator, View, Pressable } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import { Show, useObservable } from "@legendapp/state/react"

import { ar } from "@src/i18n/ar"
import { formatDateForDisplay, localDateToUnix, unixToLocalDate, trpc, formatPrice, getPaymentLabel, sharedStyles } from "@src/utils"
import { List, Header, Text, ModalWrapper, DatePicker } from "@src/components"

type Status = "active" | "cancelled"

type Filters = { status?: Status; startDate?: number; endDate?: number }

const STATUS_CONFIG: Record<Status, { color: string; label: string }> = {
  active: { color: "bg-green-500", label: ar.active },
  cancelled: { color: "bg-red-500", label: ar.cancelled },
}

export default function Returns() {
  const router = useRouter()
  const filter$ = useObservable(false)
  const [filters, setFilters] = useState<Filters>({})

  const returns = trpc.returns.list.useInfiniteQuery(
    { status: filters.status, from: filters.startDate, to: filters.endDate, limit: 25 },
    { getNextPageParam: (lastPage) => lastPage.nextCursor },
  )

  function hideFilters() {
    filter$.set(false)
  }

  const data = returns.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <>
      <Header
        title={ar.customerReturns}
        right={
          <View className="flex-row h-full">
            <Pressable onPress={() => filter$.set(true)} className="p-2 h-full justify-center active:opacity-40">
              <Icon name="filter" size={24} color="#000" />
            </Pressable>
            <Pressable onPress={() => router.push("/dashboard/returns/new")} className="p-2 h-full justify-center active:opacity-40">
              <Icon name="plus" size={24} color="#000" />
            </Pressable>
          </View>
        }
      />

      <List
        contentContainerStyle={sharedStyles.contentContainer}
        data={data}
        keyExtractor={(item) => item.id.toString()}
        onEndReached={() => (returns.hasNextPage && !returns.isFetchingNextPage ? returns.fetchNextPage() : null)}
        onRefresh={returns.refetch}
        onEndReachedThreshold={0.7}
        ListEmptyComponent={() => (
          <View className="flex-1 justify-center items-center p-8">
            {returns.isLoading ? (
              <ActivityIndicator size="large" color="#3B82F6" />
            ) : (
              <View className="items-center">
                <Icon name="keyboard-return" size={48} color="#9CA3AF" />
                <Text className="text-lg text-gray-500 mt-2">{ar.noReturns}</Text>
                <Text className="text-sm text-gray-400 text-center mt-1">{ar.addFirstReturn}</Text>
              </View>
            )}
          </View>
        )}
        renderItem={({ item }) => {
          const statusCfg = STATUS_CONFIG[item.status as Status] ?? STATUS_CONFIG.active
          const methodLabel = getPaymentLabel(item.refundMethod)

          return (
            <Pressable onPress={() => router.push(`/dashboard/returns/${item.id}`)} className="bg-white rounded-xl border border-gray-100 p-3.5 mb-3 shadow-sm active:opacity-70">
              <View className="flex-row justify-between items-start gap-4">
                <View className="flex-1">
                  <View className="flex-row items-center gap-2 mb-1.5">
                    <View className={`w-2.5 h-2.5 rounded-full ${statusCfg.color}`} />
                    <Text className="text-base font-bold text-gray-900">
                      {ar.returnNumber} #{item.id}
                    </Text>
                  </View>

                  {item.reason && (
                    <Text className="text-sm text-gray-600 mb-2.5 leading-5" numberOfLines={2}>
                      {item.reason}
                    </Text>
                  )}

                  <View className="flex-row items-center flex-wrap gap-3">
                    <View className="flex-row items-center gap-1">
                      <Icon name="clock-outline" size={13} color="#9CA3AF" />
                      <Text className="text-[11px] text-gray-400 mt-0.5">{formatDateForDisplay(item.createdAt, "D MMM • hh:mm a")}</Text>
                    </View>
                    <View className="flex-row items-center gap-1">
                      <Icon name="wallet" size={13} color="#9CA3AF" />
                      <Text className="text-[11px] text-gray-400 mt-0.5">{methodLabel}</Text>
                    </View>
                  </View>
                </View>

                <View className="items-end shrink-0">
                  <Text className="text-lg font-black text-green-600 mb-1">
                    {formatPrice(item.totalRefund)} <Text className="text-xs font-normal text-green-400">{ar.egp}</Text>
                  </Text>
                </View>
              </View>
            </Pressable>
          )
        }}
        ListFooterComponent={
          <>
            {returns.isFetchingNextPage && <ActivityIndicator size="small" className="py-2" />}
            <SafeAreaView edges={["bottom"]} />
          </>
        }
      />

      <Show if={filter$}>
        <FiltersModal hide={hideFilters} setFilters={setFilters} filters={filters} />
      </Show>
    </>
  )
}

type FiltersProps = {
  hide: () => void
  setFilters: (filters: Filters) => void
  filters: Filters
}

function FiltersModal({ hide, setFilters, filters }: FiltersProps) {
  const [status, setStatus] = useState<Status | undefined>(filters.status)
  const [startDate, setStartDate] = useState<Date | undefined>(filters.startDate ? unixToLocalDate(filters.startDate) : undefined)
  const [endDate, setEndDate] = useState<Date | undefined>(filters.endDate ? unixToLocalDate(filters.endDate) : undefined)

  function apply() {
    setFilters({
      status,
      startDate: startDate ? localDateToUnix(startDate) : undefined,
      endDate: endDate ? localDateToUnix(endDate) : undefined,
    })
    hide()
  }

  function clearFilters() {
    setStartDate(undefined)
    setEndDate(undefined)
    setStatus(undefined)
    setFilters({})
    hide()
  }

  return (
    <ModalWrapper onClose={hide}>
      <View className="flex-row items-center justify-between mb-6">
        <Text className="text-xl font-bold">{ar.filters}</Text>
        <Pressable onPress={hide} hitSlop={20} className="bg-gray-100 p-2 rounded-full active:bg-gray-200">
          <Icon name="close" size={20} color="#374151" />
        </Pressable>
      </View>

      <View className="mb-6">
        <Text className="text-sm font-bold text-gray-900 mb-3">{ar.status}</Text>
        <View className="flex-row flex-wrap gap-2">
          {(["active", "cancelled"] as const).map((s) => (
            <Pressable
              key={s}
              onPress={() => setStatus(status === s ? undefined : s)}
              className={`px-4 py-2.5 rounded-lg border flex-row items-center gap-2 ${status === s ? "bg-blue-600 border-blue-600" : "bg-white border-gray-200"}`}
            >
              {status === s && <Icon name="check" size={16} color="white" />}
              <Text className={`text-sm font-medium ${status === s ? "text-white" : "text-gray-700"}`}>{STATUS_CONFIG[s].label}</Text>
            </Pressable>
          ))}
        </View>
      </View>

      <View className="mb-8">
        <Text className="text-sm font-bold text-gray-900 mb-3">{ar.date}</Text>
        <View className="flex-row gap-3">
          <View className="flex-1">
            <Text className="text-xs text-gray-500 mb-1.5 font-medium">{ar.from}</Text>
            <View className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <DatePicker value={startDate} onChange={setStartDate} mode="date" placeholder={ar.from} />
            </View>
          </View>
          <View className="flex-1">
            <Text className="text-xs text-gray-500 mb-1.5 font-medium">{ar.to}</Text>
            <View className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <DatePicker value={endDate} onChange={setEndDate} mode="date" placeholder={ar.to} />
            </View>
          </View>
        </View>
      </View>

      <View className="flex-row gap-3">
        <Pressable onPress={clearFilters} className="flex-1 py-3.5 border border-gray-300 rounded-lg active:bg-gray-50">
          <Text className="text-center text-gray-700 font-bold">{ar.clearFilters}</Text>
        </Pressable>
        <Pressable onPress={apply} className="flex-1 py-3.5 bg-blue-600 rounded-lg active:opacity-90 shadow-sm">
          <Text className="text-center text-white font-bold">{ar.apply}</Text>
        </Pressable>
      </View>
    </ModalWrapper>
  )
}
